import { useState, useEffect, useCallback, useMemo } from "react";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { useTranslation } from "react-i18next";
import usersHelper from "../API/Users/usersHelper";
import Constants from "../Constants";
import usePolicyVersions from "./usePolicyVersions";

const useNewsletterConsent = () => {
  const [showNewsletterConsent, setShowNewsletterConsent] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { user, updateUser } = AppUser.useUser();
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const { currentPrivacyVersion } = usePolicyVersions();

  // Prefer the canonical version the website publishes in
  // /policy-versions.json; fall back to the hard-coded constant if the fetch
  // fails (offline / not yet deployed). This keeps consent records tied to
  // the actual published policy text.
  const termsVersion = useMemo(
    () => currentPrivacyVersion || Constants.newsletterConsent?.termsVersion,
    [currentPrivacyVersion],
  );

  const checkShouldShowNewsletterConsent = useCallback(async () => {
    if (!user?.id) return;
    // Only ask onboarded users (new users are asked during onboarding). The
    // modal surfaces in two cases:
    //  1. never-before-shown (newsletterPromptShown !== true) — the once
    //     per-user gate; OR
    //  2. previously consented but their recorded consent version is now
    //     stale relative to the canonical published policy version — they
    //     need to re-consent to the new terms (GDPR/re-consent on change).
    const promptNeverShown =
      user.isOnboarded && user.newsletterPromptShown !== true;
    const consentStale =
      user.isOnboarded &&
      user.subscribedToNewsletter === true &&
      !!user.newsletterConsentTermsVersion &&
      !!termsVersion &&
      user.newsletterConsentTermsVersion !== termsVersion;
    if (promptNeverShown || consentStale) {
      setShowNewsletterConsent(true);
    } else {
      setShowNewsletterConsent(false);
    }
  }, [user, termsVersion]);

  const persistNewsletterConsent = useCallback(
    async (consent) => {
      if (!user?.id) return false;
      setIsUpdating(true);
      try {
        const nowTs = Math.floor(Date.now() / 1000);
        const consentGranted = consent === true;
        await usersHelper.updateUser(user.id, [
          { property: "subscribedToNewsletter", value: consentGranted },
          { property: "newsletterConsentMethod", value: "login_popup" },
          { property: "newsletterConsentTermsVersion", value: termsVersion },
          {
            property: "newsletterPromptShown",
            value: true,
          },
          {
            property: "newsletterSubscriptionStatus",
            value: consentGranted ? "subscribed" : "pending",
          },
          { property: "newsletterUpdatedAt", value: nowTs },
          ...(consentGranted
            ? [{ property: "newsletterConsentedAt", value: nowTs }]
            : []),
        ]);

        if (updateUser) {
          updateUser({
            ...user,
            subscribedToNewsletter: consentGranted,
            newsletterConsentMethod: "login_popup",
            newsletterConsentTermsVersion: termsVersion,
            newsletterPromptShown: true,
            newsletterSubscriptionStatus: consentGranted
              ? "subscribed"
              : "pending",
            newsletterUpdatedAt: nowTs,
            ...(consentGranted && { newsletterConsentedAt: nowTs }),
          });
        }

        AppToasty.show(
          consentGranted
            ? t("newsletter.consentedToast")
            : t("newsletter.declinedToast"),
          { placement: "top", type: consentGranted ? "success" : "warning" }
        );
        return true;
      } catch (error) {
        console.error("Error persisting newsletter consent:", error);
        AppToasty.show(t("newsletter.updateFailed"), {
          placement: "top",
          type: "danger",
        });
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [user, updateUser, AppToasty, t, termsVersion]
  );

  const handleAccept = useCallback(async () => {
    const ok = await persistNewsletterConsent(true);
    if (ok) setShowNewsletterConsent(false);
  }, [persistNewsletterConsent]);

  const handleDecline = useCallback(async () => {
    const ok = await persistNewsletterConsent(false);
    if (ok) setShowNewsletterConsent(false);
  }, [persistNewsletterConsent]);

  const handleSkip = useCallback(async () => {
    // Even when dismissed, record the prompt so it never repeats (once-only).
    const ok = await persistNewsletterConsent(false);
    if (ok) setShowNewsletterConsent(false);
  }, [persistNewsletterConsent]);

  useEffect(() => {
    checkShouldShowNewsletterConsent();
  }, [checkShouldShowNewsletterConsent]);

  return {
    showNewsletterConsent,
    setShowNewsletterConsent,
    isUpdating,
    handleAccept,
    handleDecline,
    handleSkip,
    checkShouldShowNewsletterConsent,
  };
};

export default useNewsletterConsent;